import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_URL } from '../config/apiConfig';

// ─── Path → Friendly Screen Name Mapping ───────────────────────────────────

const SCREEN_MAP = [
  { path: '/', exact: true, label: 'Home' },
  { path: '/login', exact: true, label: 'Login' },
  { path: '/signup', exact: true, label: 'Sign Up' },
  { path: '/forgot-password', exact: true, label: 'Forgot Password' },
  { path: '/reset-password', exact: true, label: 'Reset Password' },
  { path: '/change-password', exact: true, label: 'Change Password' },
  { path: '/profile', exact: true, label: 'My Profile' },
  { path: '/account', exact: true, label: 'Account' },
  { path: '/my-collection', exact: true, label: 'My Collection' },
  { path: '/public-info', exact: true, label: 'About AmpNebula' },
  { path: '/manufacturers', exact: true, label: 'Manufacturers' },
  { path: '/suppliers', exact: true, label: 'Suppliers' },
  { path: '/component_categories', exact: true, label: 'Component Categories' },
  { path: '/component_subcategories', exact: true, label: 'Component Subcategories' },
  { path: '/component_screen', exact: true, label: 'Components' },
  { path: '/amplifier_screen', exact: true, label: 'Amplifiers' },
  { path: '/amplifier_search', exact: true, label: 'Search Amplifiers' },
  { path: '/amplifier_components_screen', exact: true, label: 'Amplifier Details' },
  { path: '/amplifier-components/', exact: false, label: 'Amplifier Details' },
  { path: '/amps/', exact: false, label: 'Amplifier Details' },
  { path: '/templates', exact: true, label: 'Templates' },
  { path: '/discussions/topic/', exact: false, label: 'Discussion Topic' },
  { path: '/discussions', exact: true, label: 'Discussions' },
  { path: '/discussion-admin', exact: true, label: 'Discussion Admin' },
  { path: '/discussion-tree-admin', exact: true, label: 'Discussion Tree Admin' },
  { path: '/user-management', exact: true, label: 'User Management' },
  { path: '/role-management', exact: true, label: 'Role Management' },
  { path: '/screen-management', exact: true, label: 'Screen Management' },
  { path: '/site-admin', exact: true, label: 'Site Administration' },
  { path: '/amplifier-hierarchy', exact: true, label: 'Amplifier Hierarchy' },
  { path: '/duplicate-cleanup', exact: true, label: 'Duplicate Cleanup' },
  { path: '/error-log', exact: true, label: 'Error Log' },
  { path: '/activity-log', exact: true, label: 'Activity Log' },
  { path: '/feedback-admin', exact: true, label: 'Feedback Admin' },
  { path: '/collection/', exact: false, label: 'Shared Collection' },
  { path: '/verify/', exact: false, label: 'Certificate Verification' },
];

function getScreenName(pathname) {
  // Try exact matches first
  for (const screen of SCREEN_MAP) {
    if (screen.exact && pathname === screen.path) return screen.label;
  }
  // Then prefix matches
  for (const screen of SCREEN_MAP) {
    if (!screen.exact && pathname.startsWith(screen.path)) return screen.label;
  }
  return null;
}

// ─── Component ──────────────────────────────────────────────────────────────

const NavigationTracker = () => {
  const location = useLocation();
  const { token } = useAuth();
  const prevPathRef = useRef(null);

  useEffect(() => {
    const pathname = location.pathname;

    // Don't log the same path twice in a row
    if (pathname === prevPathRef.current) return;
    prevPathRef.current = pathname;

    const screenName = getScreenName(pathname);
    if (!screenName) return; // Unknown page, skip

    // Fire-and-forget POST to track the navigation (works for both auth and anonymous)
    const api = axios.create({ baseURL: API_URL });
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    api.post('/activity/track', {
      action: 'page_view',
      entity_type: 'page',
      entity_name: screenName,
      description: `Navigated to ${screenName}`,
      metadata: { url_path: pathname + location.search }
    }).catch(() => {
      // Silently ignore — never break the app for activity tracking
    });
  }, [location, token]);

  return null;
};

export default NavigationTracker;
